import React, { useEffect, useState } from "react";
import {
  fetchSpecialties,
  createSpecialty,
  updateSpecialty,
  deleteSpecialty,
} from "../APIs/specialtyUI";

function SpecialtyTab() {
  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState({ id: null, name: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSpecialties();
  }, []);

  async function loadSpecialties() {
    try {
      setLoading(true);
      const data = await fetchSpecialties();
      setSpecialties(data);
    } catch (err) {
      console.error("Failed to fetch specialties", err);
      alert("Failed to load specialties.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }

    try {
      if (isEditing) {
        const updated = await updateSpecialty(form.id, { name: form.name });
        setSpecialties((prev) =>
          prev.map((sp) => (sp.id === form.id ? updated : sp)),
        );
        setIsEditing(false);
      } else {
        const created = await createSpecialty({ name: form.name });
        setSpecialties((prev) => [...prev, created]);
      }

      setForm({ id: null, name: "" });
    } catch (err) {
      console.error("Failed to save specialty", err);
      alert(err.response?.data?.message || "Error saving specialty.");
    }
  }

  function handleEdit(sp) {
    setForm(sp);
    setIsEditing(true);
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this specialty?"))
      return;

    try {
      await deleteSpecialty(id);
      setSpecialties((prev) => prev.filter((sp) => sp.id !== id));
    } catch (err) {
      console.error("Failed to delete specialty", err);
      alert("Error deleting specialty.");
    }
  }

  function handleCancel() {
    setForm({ id: null, name: "" });
    setIsEditing(false);
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-3">Specialties</h2>

      <form
        onSubmit={handleSubmit}
        className="mb-6 max-w-md bg-gray-50 p-4 rounded shadow"
      >
        <div className="mb-3">
          <label className="block font-medium mb-1" htmlFor="name">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
            required
          />
        </div>

        <div className="flex space-x-2">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            {isEditing ? "Update" : "Add"}
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <table className="min-w-full border border-gray-300 rounded shadow">
        <thead className="bg-gray-100">
          <tr>
            <th className="border px-4 py-2 text-left">ID</th>
            <th className="border px-4 py-2 text-left">Name</th>
            <th className="border px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={3} className="text-center p-4 text-gray-500">
                Loading...
              </td>
            </tr>
          ) : specialties.length === 0 ? (
            <tr>
              <td colSpan={3} className="text-center p-4 text-gray-500">
                No specialties found.
              </td>
            </tr>
          ) : (
            specialties.map((sp) => (
              <tr key={sp.id} className="hover:bg-gray-50">
                <td className="border px-4 py-2">{sp.id}</td>
                <td className="border px-4 py-2">{sp.name}</td>
                <td className="border px-4 py-2 text-center space-x-2">
                  <button
                    onClick={() => handleEdit(sp)}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(sp.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default SpecialtyTab;
